import type { IVector3 } from '@seddi/math-v2';
import { type Fabric, FabricSide } from '@seddi/scene';
import type { LoadingManager, WebGLRenderer } from '@seddi/three';
import type { RecolorContext } from '@seddi/ui-recolor';
import { produce } from 'immer';

import { adapt } from './fabricAdapter';
import type { FabricGfx } from './fabricTypes';
import { load as loadMaterial, loadRecolor as loadMaterialRecolor } from './materialLoader';

/**
 * Load fabric.
 * @param desc - Fabric.
 * @param manager - Loading manager.
 * @param renderer - WebGL renderer.
 */
export const loadRaw = async (
  desc: Fabric,
  manager: LoadingManager,
  renderer: WebGLRenderer,
): Promise<FabricGfx> => {
  const doubleSided = adapt(desc);
  const mdaterialDescs = [doubleSided.front];
  if (doubleSided.back) {
    mdaterialDescs.push(doubleSided.back);
  }
  // Load materials.
  const promises = mdaterialDescs.map((materialDesc) => loadMaterial(materialDesc, manager, renderer));
  const materials = await Promise.all(promises);

  const fabric: FabricGfx = { front: materials[0] };
  if (materials.length === 2) {
    fabric.back = materials[1];
  }
  return fabric;
};

/**
 * Load fabric with albedo recolor.
 * @param desc - Fabric.
 * @param manager Loading manager.
 * @param renderer - WebGL renderer.
 * @param color - Target color.
 * @param recolor - Recolor context.
 */
export const loadRecolor = async (
  desc: Fabric,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  color: IVector3,
  recolor: RecolorContext,
): Promise<FabricGfx> => {
  const doubleSided = adapt(desc);
  const materialDescs = [doubleSided.front];
  if (doubleSided.back) {
    materialDescs.push(doubleSided.back);
  }
  // NOTE: ID comes formatted with color, validate it makes sense outside this function.
  const fabricId = desc.id;
  // Load materials
  const promises = materialDescs.map(async (materialDesc, i) => {
    // NOTE: Validate if textures can come with colliding IDs.
    const materialDesc_ = produce(materialDesc, (materialDesc) => {
      const _id = `${fabricId.split(':')[0]}_${i === 0 ? FabricSide.FRONT : FabricSide.BACK}`;
      materialDesc.params.baseColorMap!.id = _id;
    });
    return loadMaterialRecolor(materialDesc_, manager, renderer, color, recolor);
  });
  const materials = await Promise.all(promises);

  const fabric: FabricGfx = { front: materials[0] };
  if (materials.length === 2) {
    fabric.back = materials[1];
  }
  return fabric;
};

/**
 * Load fabric with optional albedo recolor.
 * @param desc - Fabric.
 * @param manager - Loading manager.
 * @param renderer - WebGL renderer.
 * @param color - Target color.
 * @param recolor - Recolor context.
 */
export const load = (
  desc: Fabric,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  color?: IVector3,
  recolor?: RecolorContext,
) => {
  if (color) {
    return loadRecolor(desc, manager, renderer, color, recolor!);
  }
  return loadRaw(desc, manager, renderer);
};
